import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type StorageBreakdown } from "../../lib/api";
import { formatBytes } from "../../lib/format";
import { ConfirmDialog } from "../ConfirmDialog";

// StorageSection shows a breakdown of disk usage in the agent's data
// directory and offers a one-click purge of delivered artifacts past a
// configurable age. Built and failed artifacts are left alone — those
// still represent state the operator may need.
export function StorageSection() {
  const qc = useQueryClient();
  const [days, setDays] = useState(30);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);
  const [confirmPurge, setConfirmPurge] = useState(false);

  const storage = useQuery({
    queryKey: ["storage"],
    queryFn: () => api.get<StorageBreakdown>("/api/v1/storage"),
  });

  const purge = useMutation({
    mutationFn: () =>
      api.post<{ deletedCount: number; freedBytes: number }>(
        "/api/v1/storage/purge",
        { olderThanDays: days },
      ),
    onSuccess: (res) => {
      setPurgeResult(
        res.deletedCount === 0
          ? "Nothing to purge — no delivered artifacts older than the cutoff."
          : `Removed ${res.deletedCount} artifact${res.deletedCount === 1 ? "" : "s"} (${formatBytes(res.freedBytes)} freed).`,
      );
      qc.invalidateQueries({ queryKey: ["storage"] });
      qc.invalidateQueries({ queryKey: ["artifacts"] });
    },
  });

  const data = storage.data;
  const total = data?.totalBytes ?? 0;
  const segments = data
    ? [
        { label: "Database", bytes: data.dbBytes, color: "bg-brand-500" },
        {
          label: "Artifacts",
          bytes: data.artifactsBytes,
          color: "bg-emerald-500",
        },
        { label: "Uploads", bytes: data.uploadsBytes, color: "bg-amber-500" },
        { label: "Run logs", bytes: data.runLogsBytes, color: "bg-slate-400" },
      ]
    : [];

  return (
    <div className="space-y-6">
      <section className="card overflow-hidden">
        <div className="border-b border-rule px-6 py-4">
          <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
            Disk usage
          </h2>
          <p className="mt-1 text-[13px] text-ash">
            Bytes occupied by the agent's persistent state, broken down by
            category.
          </p>
        </div>
        {storage.isLoading ? (
          <p className="px-6 py-8 text-sm text-ash">Loading…</p>
        ) : data ? (
          <div className="px-6 py-5">
            <div className="flex items-baseline gap-3">
              <span className="font-display text-[32px] font-semibold tracking-tight text-ink">
                {formatBytes(total)}
              </span>
              <span className="font-mono text-[11px] text-ash">
                {data.dataDir}
              </span>
            </div>
            <div className="mt-4 flex h-2 w-full overflow-hidden rounded bg-rule">
              {segments.map((s) => {
                const pct = total > 0 ? (s.bytes / total) * 100 : 0;
                if (pct < 0.1) return null;
                return (
                  <div
                    key={s.label}
                    className={s.color}
                    style={{ width: `${pct}%` }}
                    title={`${s.label}: ${formatBytes(s.bytes)}`}
                  />
                );
              })}
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
              {segments.map((s) => (
                <div key={s.label}>
                  <dt className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                    <span className={`h-2 w-2 rounded-full ${s.color}`} />
                    {s.label}
                  </dt>
                  <dd className="mt-1 font-mono text-[14px] text-ink">
                    {formatBytes(s.bytes)}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 grid grid-cols-2 gap-4 border-t border-rule pt-4 text-sm md:grid-cols-3">
              <Row label="Artifacts" value={`${data.artifactsCount}`} />
              <Row label="Uploaded files" value={`${data.uploadsCount}`} />
              {data.artifactsByState && (
                <Row
                  label="By state"
                  value={Object.entries(data.artifactsByState)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(" · ")}
                />
              )}
            </div>
          </div>
        ) : null}
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-rule px-6 py-4">
          <h2 className="font-display text-[18px] font-semibold tracking-tight text-ink">
            Purge old artifacts
          </h2>
          <p className="mt-1 max-w-2xl text-[13px] text-ash">
            Removes <span className="font-semibold text-ink">delivered</span>{" "}
            artifacts older than the cutoff. Built and failed artifacts are left
            alone — the central catalog is the source of truth for the data, so
            once an artifact has shipped you can free the local copy.
          </p>
        </div>
        <div className="px-6 py-5">
          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Older than
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) =>
                    setDays(Math.max(1, Number(e.target.value) || 30))
                  }
                  className="input w-24"
                />
                <span className="text-[13px] text-ink">days</span>
              </div>
            </label>
            <button
              onClick={() => setConfirmPurge(true)}
              disabled={purge.isPending}
              className="btn-primary"
            >
              {purge.isPending ? "Purging…" : "Purge now"}
            </button>
          </div>
          {purgeResult && (
            <p className="mt-4 text-[13px] text-ink">{purgeResult}</p>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={confirmPurge}
        title="Purge delivered artifacts"
        description={`Purge delivered artifacts older than ${days} day${days === 1 ? "" : "s"}? This cannot be undone.`}
        confirmLabel="Purge"
        onConfirm={() => {
          setPurgeResult(null);
          purge.mutate();
          setConfirmPurge(false);
        }}
        onCancel={() => setConfirmPurge(false)}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
        {label}
      </dt>
      <dd className="mt-1 text-[14px] text-ink">{value}</dd>
    </div>
  );
}
